import React, { useState, useEffect, useRef } from 'react';
import './Frameworks.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import Swal from 'sweetalert2';
import { getOrgPermissions } from '../../utils/permissions';
import { isSoc2, TSC_LABEL_KEYS } from '../../utils/tsc';
import { peekSession } from '../../utils/peek';

function FrameworksPage() {
  const [frameworks, setFrameworks]       = useState([]);
  const [orgFrameworks, setOrgFrameworks] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [filter, setFilter]               = useState('all');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAdmin = localStorage.getItem('role') === 'ROLE_PLATFORM_ADMIN';
  const { canManageFrameworks } = getOrgPermissions();
  // T-0532: guards against a double-click/re-open of the add-on confirm dialog firing two checkout
  // requests before the first Stripe redirect happens (Swal's .then callback isn't a preConfirm, so
  // nothing disables the confirm button while the request is in flight). Backed by a matching
  // server-side PENDING claim (BillingService.createFrameworkAddonCheckoutSession), this is just the
  // fast client-side path that avoids a confusing 409 error toast for the common accidental case.
  const addonPurchaseInFlight = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        // A platform admin peeking into a client org keeps ROLE_PLATFORM_ADMIN, and /users/username/{me}
        // would return the admin's own org, so read the peeked org directly.
        const peek = peekSession();
        const [fwRes, orgRes] = await Promise.all([
          axiosInstance.get('/frameworks'),
          peek ? axiosInstance.get(`/organization/${peek.orgId}`)
            : isAdmin ? Promise.resolve(null)
              : axiosInstance.get(`/users/username/${localStorage.getItem('userName')}`),
        ]);
        setFrameworks(fwRes.data || []);
        if (orgRes) {
          setOrgFrameworks((peek ? orgRes.data : orgRes.data?.organization)?.frameworks || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAdmin]);

  // Returned from Stripe after paying for an à-la-carte framework add-on. The webhook enrolls the org
  // (may lag a second or two), so confirm success and clean the flag from the URL.
  useEffect(() => {
    if (window.location.hash.includes('added=1')) {
      Swal.fire({
        title: t('Frameworks.addonPaidTitle', 'Framework added'),
        text: t('Frameworks.addonPaidText', 'Payment received — your new framework is being set up and will appear here shortly.'),
        icon: 'success',
        confirmButtonText: t('Frameworks.ok', 'OK'),
      });
      window.history.replaceState(null, '', window.location.hash.replace(/\?added=1$/, '').replace('added=1', ''));
    }
  }, [t]);

  const isEnrolled = (fw) => orgFrameworks.some(o => o.id === fw.id);

  // SOC 2 enrollment goes through a criteria-selection step first
  const SOC2_PILLARS = [
    { code: 'A1',  labelKey: TSC_LABEL_KEYS.A1 },
    { code: 'C1',  labelKey: TSC_LABEL_KEYS.C1 },
    { code: 'PI1', labelKey: TSC_LABEL_KEYS.PI },
    { code: 'P',   labelKey: TSC_LABEL_KEYS.P },
  ];
  const ONBOARDING_STEPS = [
    'Creating your audit workspace',
    'Adding your compliance tests',
    'Mapping controls to your framework',
    'Preparing your policy templates',
    'Finalizing your program',
  ];
  const [enrollTarget, setEnrollTarget] = useState(null); // SOC 2 framework awaiting criteria selection
  const [tscChecked, setTscChecked]     = useState({});
  const [enrolling, setEnrolling]       = useState(false);
  const [onboarding, setOnboarding]     = useState(null); // { framework, step, done } during signup

  const handleSignUp = (fw) => {
    if (isSoc2(fw)) {
      setTscChecked({});
      setEnrollTarget(fw);
      return;
    }
    doSignUp(fw.id, null, fw.displayName);
  };

  const confirmSoc2Enroll = () => {
    if (!enrollTarget) return;
    const csv = ['CC', ...SOC2_PILLARS.filter(pl => tscChecked[pl.code]).map(pl => pl.code)].join(',');
    doSignUp(enrollTarget.id, csv, enrollTarget.displayName);
  };

  const doSignUp = async (frameworkId, tscSelection, frameworkName) => {
    setEnrolling(true);
    setEnrollTarget(null);
    // Enrollment is a multi-second synchronous call (creates the audit, seeds tests, maps controls,
    // copies policy templates). Show a full-screen staged progress overlay so the user stays engaged
    // and can't navigate into a not-yet-committed empty workspace.
    setOnboarding({ framework: frameworkName || 'your framework', step: 0, done: false });
    const timer = setInterval(() => {
      setOnboarding(o => (o && !o.done && o.step < ONBOARDING_STEPS.length - 1) ? { ...o, step: o.step + 1 } : o);
    }, 7000);
    try {
      await axiosInstance.post(`/organization/${frameworkId}/framework-signup`, {}, {
        params: tscSelection ? { tscSelection } : {},
      });
      clearInterval(timer);
      setOrgFrameworks(prev => [...prev, { id: frameworkId }]);
      setOnboarding(o => ({ framework: (o && o.framework) || frameworkName || 'your framework', step: ONBOARDING_STEPS.length - 1, done: true }));
    } catch (e) {
      clearInterval(timer);
      setOnboarding(null);
      setEnrollTarget(null);
      // Pre-paid wall: the backend returns a STRUCTURED 402. reason=FRAMEWORK_LIMIT means the org is
      // subscribed but at its plan's framework cap — offer the à-la-carte add-on ($149/mo) OR a plan
      // upgrade instead of a dead-end. reason=NO_SUBSCRIPTION means send them to Billing to subscribe.
      if (e?.response?.status === 402) {
        const data = e.response?.data || {};
        const serverMsg = data.message || (typeof e.response?.data === 'string' ? e.response.data : null);
        const billingPath = `${isAdmin ? '/adminHome' : '/dashboard'}/billing`;
        if (data.reason === 'FRAMEWORK_LIMIT') {
          const price = data.alacartePrice != null ? Number(data.alacartePrice) : 149;
          const fee   = Number(data.alacarteFee) > 0 ? Number(data.alacarteFee) : 0;
          const feeNote = fee > 0
            ? t('Frameworks.addonFeeNote', { fee: fee.toFixed(2), defaultValue: `Plus a $${fee.toFixed(2)} payment processing fee (card processing costs, passed through at cost) — total $${(price + fee).toFixed(2)}/mo.` })
            : '';
          const name  = data.frameworkName || frameworkName || t('Frameworks.thisFramework', 'this framework');
          Swal.fire({
            title: t('Frameworks.addFrameworkTitle', 'Add another framework'),
            html: (serverMsg || t('Frameworks.frameworkLimitText', {
              name, price,
              defaultValue: `You're at your plan's framework limit. Add ${name} à-la-carte for $${price}/mo, or upgrade your plan for more.`,
            })) + (feeNote ? `<br/><small>${feeNote}</small>` : ''),
            icon: 'info',
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: t('Frameworks.addForPrice', { name, price: fee > 0 ? (price + fee).toFixed(2) : price, defaultValue: `Add ${name} — $${fee > 0 ? (price + fee).toFixed(2) : price}/mo` }),
            denyButtonText: t('Frameworks.upgradePlan', 'Upgrade plan'),
            cancelButtonText: t('Frameworks.cancel', 'Cancel'),
          }).then(async (r) => {
            if (r.isConfirmed) {
              if (addonPurchaseInFlight.current) return;
              addonPurchaseInFlight.current = true;
              // Adding a framework to the subscription also seeds its audit, which can take up to a
              // minute. Hold a non-dismissable spinner so the user can't re-submit (a retry would trip
              // the "already added" guard) and knows it's working.
              Swal.fire({
                title: t('Frameworks.addonAddingTitle', 'Adding framework…'),
                html: t('Frameworks.addonAddingText', 'Setting up your framework and billing — this can take up to a minute.'),
                allowOutsideClick: false, allowEscapeKey: false,
                didOpen: () => Swal.showLoading(),
              });
              try {
                const res = await axiosInstance.post(`/billing/framework-addon/${frameworkId}/checkout`);
                // Recurring path (org already on a Stripe subscription): the framework is added as a
                // subscription item server-side — no redirect. Confirm + mark it enrolled.
                if (res.data?.added) {
                  setOrgFrameworks(prev => [...prev, { id: frameworkId }]);
                  Swal.fire({
                    title: t('Frameworks.addonAddedTitle', 'Framework added'),
                    text: res.data.message || t('Frameworks.addonAddedText', 'Added to your subscription and enrolled.'),
                    icon: 'success', confirmButtonText: t('Frameworks.ok', 'OK'),
                  });
                  return;
                }
                // One-time path (grandfathered / no Stripe subscription): redirect to Stripe Checkout.
                if (res.data?.checkoutUrl) { window.location.href = res.data.checkoutUrl; return; }
                Swal.fire({ title: t('Frameworks.errorTitle'), text: t('Frameworks.signUpErrorText'), icon: 'error', confirmButtonText: t('Frameworks.ok') });
              } catch (err) {
                // A slow add may have actually landed (client gave up first) — re-check enrollment before
                // showing an error, and treat "already being added" (409) as success.
                let landed = err?.response?.status === 409;
                try {
                  const chk = await axiosInstance.get(`/users/username/${localStorage.getItem('userName')}`);
                  const fws = chk.data?.organization?.frameworks || [];
                  if (fws.some(f => f.id === frameworkId)) landed = true;
                } catch { /* ignore */ }
                if (landed) {
                  setOrgFrameworks(prev => prev.some(f => f.id === frameworkId) ? prev : [...prev, { id: frameworkId }]);
                  Swal.fire({ title: t('Frameworks.addonAddedTitle', 'Framework added'),
                    text: t('Frameworks.addonAddedText', 'Added to your subscription and enrolled.'),
                    icon: 'success', confirmButtonText: t('Frameworks.ok', 'OK') });
                } else {
                  const em = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null);
                  Swal.fire({ title: t('Frameworks.errorTitle'), text: em || t('Frameworks.signUpErrorText'), icon: 'error', confirmButtonText: t('Frameworks.ok') });
                }
              } finally {
                addonPurchaseInFlight.current = false;
              }
            } else if (r.isDenied) {
              navigate(billingPath);
            }
          });
        } else {
          Swal.fire({
            title: t('Frameworks.subscriptionRequiredTitle', 'Subscription required'),
            text: serverMsg || t('Frameworks.subscriptionRequiredText', 'A subscription is required to enrol in a framework. Please subscribe to a plan to continue.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Frameworks.goToBilling', 'Go to Billing'),
            cancelButtonText: t('Frameworks.cancel', 'Cancel'),
          }).then(r => { if (r.isConfirmed) navigate(billingPath); });
        }
      } else {
        Swal.fire({ title: t('Frameworks.errorTitle'), text: t('Frameworks.signUpErrorText'), icon: 'error', confirmButtonText: t('Frameworks.ok') });
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleView = (frameworkId) => {
    const base = isAdmin ? '/adminHome' : '/dashboard';
    navigate(`${base}/compliance/frameworks/${frameworkId}/requirements`);
  };

  if (loading) return <div className="fw-loading"><div className="fw-spinner" /></div>;
  if (error)   return <div className="fw-error">{t('Frameworks.errorPrefix', { error })}</div>;

  const activeCount    = frameworks.filter(isEnrolled).length;
  const availableCount = frameworks.length - activeCount;

  const displayed = filter === 'active'    ? frameworks.filter(fw =>  isEnrolled(fw))
                  : filter === 'available' ? frameworks.filter(fw => !isEnrolled(fw))
                  : frameworks;

  return (
    <div className="fw-page">
      {/* Stat bar + filters */}
      <div className="fw-stat-bar">
        <div className="fw-stat">
          <span className="fw-stat-val">{frameworks.length}</span>
          <span className="fw-stat-label">{t('Frameworks.total')}</span>
        </div>
        <div className="fw-stat">
          <span className="fw-stat-val fw-val-ok">{activeCount}</span>
          <span className="fw-stat-label">{t('Frameworks.enrolled')}</span>
        </div>
        <div className="fw-stat">
          <span className="fw-stat-val">{availableCount}</span>
          <span className="fw-stat-label">{t('Frameworks.available')}</span>
        </div>
        <div className="fw-spacer" />
        <div className="fw-filters">
          {['all', 'active', 'available'].map(f => (
            <button
              key={f}
              className={`fw-filter-btn ${filter === f ? 'fw-filter-active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {t(`Frameworks.filter_${f}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Framework grid */}
      {displayed.length === 0 ? (
        <div className="fw-empty">{t('Frameworks.emptyState')}</div>
      ) : (
        <div className="fw-grid">
          {displayed.map(fw => {
            const enrolled = isEnrolled(fw);
            return (
              <div key={fw.id} className={`fw-card ${enrolled ? 'fw-card-enrolled' : ''}`}>
                <div className="fw-card-top">
                  <div className="fw-avatar">{fw.displayName.charAt(0)}</div>
                  <div className="fw-card-meta">
                    <span className="fw-card-name">{fw.displayName}</span>
                    {enrolled && (
                      <span className="fw-enrolled-badge">
                        <i className="fas fa-check-circle" /> {t('Frameworks.enrolledBadge')}
                      </span>
                    )}
                  </div>
                </div>
                <p className="fw-card-desc">
                  {fw.description || t('Frameworks.noDescription')}
                </p>
                <div className="fw-card-actions">
                  {(isAdmin || enrolled) ? (
                    <button className="fw-btn-primary" onClick={() => handleView(fw.id)}>
                      {t('Frameworks.viewRequirements')} <i className="fas fa-arrow-right" />
                    </button>
                  ) : canManageFrameworks ? (
                    <button className="fw-btn-enroll" onClick={() => handleSignUp(fw)} disabled={enrolling}>
                      {t('Frameworks.getStarted')} <i className="fas fa-plus" />
                    </button>
                  ) : (
                    <span className="fw-no-access">
                      <i className="fas fa-lock" /> {t('Frameworks.adminAccessRequired')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SOC 2 criteria selection before enrollment */}
      {enrollTarget && (
        <div className="fw-overlay" onClick={() => !enrolling && setEnrollTarget(null)}>
          <div className="fw-modal" onClick={e => e.stopPropagation()}>
            <div className="fw-modal-header">
              <span>{t('Frameworks.criteriaModalTitle', { framework: enrollTarget.displayName })}</span>
              <button className="fw-modal-close" onClick={() => setEnrollTarget(null)} disabled={enrolling}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="fw-modal-body">
              <p className="fw-modal-sub">{t('Frameworks.criteriaModalSub')}</p>
              <div className="fw-tsc-grid">
                <label className="fw-tsc-item fw-tsc-locked">
                  <input type="checkbox" checked disabled />
                  <span>
                    <strong>{t(TSC_LABEL_KEYS.CC)}</strong>
                    <em>{t('Frameworks.criteriaMandatory')}</em>
                  </span>
                </label>
                {SOC2_PILLARS.map(pl => (
                  <label key={pl.code} className="fw-tsc-item">
                    <input type="checkbox" checked={!!tscChecked[pl.code]}
                      onChange={e => setTscChecked(prev => ({ ...prev, [pl.code]: e.target.checked }))} />
                    <span>
                      <strong>{t(pl.labelKey)}</strong>
                      <em>{pl.code}</em>
                    </span>
                  </label>
                ))}
              </div>
              <p className="fw-modal-hint">{t('Frameworks.criteriaHint')}</p>
            </div>
            <div className="fw-modal-footer">
              <button className="fw-btn-secondary" onClick={() => setEnrollTarget(null)} disabled={enrolling}>
                {t('Frameworks.cancel', 'Cancel')}
              </button>
              <button className="fw-btn-primary" onClick={confirmSoc2Enroll} disabled={enrolling}>
                {enrolling
                  ? <><i className="fas fa-spinner fa-spin" /> {t('Frameworks.enrolling')}</>
                  : t('Frameworks.enrollWithScope')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding progress overlay — shown while the (synchronous) framework signup runs. */}
      {onboarding && (
        <div className="fw-overlay" style={{ zIndex: 4000 }}>
          <div className="fw-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            {!onboarding.done ? (
              <div style={{ padding: '34px 30px' }}>
                <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                  {t('Frameworks.onboardingTitle', { framework: onboarding.framework })}
                </h3>
                <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 22px' }}>
                  {t('Frameworks.onboardingSub')}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {ONBOARDING_STEPS.map((label, i) => {
                    const state = i < onboarding.step ? 'done' : i === onboarding.step ? 'active' : 'todo';
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11, fontSize: 14,
                        color: state === 'done' ? '#059669' : state === 'active' ? '#0f172a' : '#94a3b8',
                        fontWeight: state === 'active' ? 600 : 400 }}>
                        {state === 'done'
                          ? <i className="fas fa-check-circle" style={{ color: '#059669', width: 18 }} />
                          : state === 'active'
                            ? <i className="fas fa-spinner fa-spin" style={{ color: '#4f46e5', width: 18 }} />
                            : <i className="far fa-circle" style={{ color: '#cbd5e1', width: 18 }} />}
                        <span>{label}{state === 'active' ? '\u2026' : ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ padding: '38px 30px', textAlign: 'center' }}>
                <i className="fas fa-circle-check" style={{ fontSize: 46, color: '#059669', marginBottom: 14 }} />
                <h3 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 700, color: '#0f172a' }}>
                  {t('Frameworks.onboardingDoneTitle')}
                </h3>
                <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 24px' }}>
                  {t('Frameworks.onboardingDoneSub', { framework: onboarding.framework })}
                </p>
                <button className="fw-btn-primary" onClick={() => { setOnboarding(null); navigate(`${isAdmin ? '/adminHome' : '/dashboard'}/tests`); }}>
                  {t('Frameworks.onboardingGoToWorkspace')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FrameworksPage;
